import { useEffect, useState } from 'react';

/**
 * Tracks which `[data-sec]` heading the reader is currently level with, so the
 * contents list can highlight it.
 *
 * Ported from the source design's scroll handler, with three changes:
 *   - work is deferred to an animation frame, so a fast scroll cannot queue up
 *     dozens of layout reads;
 *   - the listener is passive, so it never blocks scrolling;
 *   - it re-measures on resize, because section offsets move when the layout
 *     reflows at a breakpoint.
 *
 * Returns `null` before the first measurement, which callers treat as
 * "highlight the first entry".
 */

/** How far below the viewport top a heading must come to count as current. */
const THRESHOLD = 170;

/**
 * A little further than the last heading strictly needs, so it holds the
 * highlight for the final stretch of scrolling rather than only on the last
 * pixel - scroll offsets are fractional on a trackpad and at non-integer zoom.
 */
const TAIL_CLEARANCE = 80;

/**
 * Treated as "scrolled to the end".
 *
 * Not 1px. `scrollHeight` is an integer while the scroll offset and the layout
 * behind it are fractional, so at the bottom of the page the two disagree by a
 * pixel or two on a fractional device ratio or at browser zoom - and CI, on
 * different fonts and a different renderer, disagreed by enough to skip the
 * clause-the-reader-clicked branch below entirely.
 */
const END_EPSILON = 8;

export function useScrollSpy(sectionCount: number): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;
      const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-sec]'));
      if (sections.length === 0) {
        setActive(null);
        return;
      }

      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const remaining = maxScroll - window.scrollY;

      /*
       * A HEADING NEAR THE END OF THE PAGE CANNOT REACH A FIXED LINE.
       *
       * The old rule was "current = the last heading above scrollY + 170". On the
       * longer policies the final clauses are short, and the document simply runs
       * out of scroll before their headings get that high: on /privacy, §10 needed
       * a scroll offset of 5063 against a maximum of 4866. Those entries could
       * never highlight, however far you scrolled, and clicking one appeared to do
       * nothing.
       *
       * So the line slides - but only by the shortfall, and only over the last
       * stretch of scrolling equal to it. `deficit` is exactly how far past the
       * fixed line the final heading ends up when the page has bottomed out, so a
       * page with nothing stranded computes zero here and behaves as it always did.
       *
       * Sliding by more than the shortfall is a bug, not a safety margin: an
       * earlier attempt slid a whole screenful, which swept the line past clauses
       * that were perfectly reachable and left clicking §8 highlighting §10.
       *
       * The alternative - padding the article until every heading can reach 170px
       * - was rejected: the amount needed grows with viewport height, which would
       * have meant several hundred pixels of blank space before the footer on a
       * large display, to fix a highlight.
       */
      const lastTop =
        (sections[sections.length - 1]?.getBoundingClientRect().top ?? 0) + window.scrollY;
      const deficit = Math.max(0, lastTop + TAIL_CLEARANCE - THRESHOLD - maxScroll);
      const boost = Math.min(deficit, Math.max(0, deficit - remaining));
      const line = window.scrollY + THRESHOLD + boost;

      let current = sections[0]?.dataset.sec ?? null;
      for (const section of sections) {
        if (section.getBoundingClientRect().top + window.scrollY <= line) {
          current = section.dataset.sec ?? current;
        }
      }

      /*
       * At the very bottom, a heading the reader asked for outranks the sliding
       * line. Clicking a tail entry scrolls as far as the page will go, which is
       * the same place every other tail entry ends up, so position alone cannot
       * tell which one was wanted - but the hash can.
       *
       * Deliberately narrow: it applies only at the end of the document, and only
       * to a heading the plain threshold could not have reached anyway. A stale
       * hash from a clause further up is ignored, because that clause highlights
       * perfectly well on its own.
       */
      if (remaining <= END_EPSILON) {
        const wanted = window.location.hash.slice(1);
        const target = wanted
          ? sections.find((section) => section.dataset.sec === wanted)
          : undefined;

        if (target) {
          const top = target.getBoundingClientRect().top + window.scrollY;
          if (top > maxScroll + THRESHOLD) current = wanted;
        }
      }

      setActive(current);
    };

    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    // Clicking a contents entry changes the hash without necessarily changing the
    // scroll offset, once the page is already at the bottom.
    window.addEventListener('hashchange', schedule);

    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('hashchange', schedule);
    };
  }, [sectionCount]);

  return active;
}
